import React, { useState } from 'react';
import { parseDate, formatDate, calculateDays } from '../utils/dateUtils';

interface ExtendRentalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExtend: (newEndDate: Date, newEndTime: string) => void;
  currentEndDate: Date;
  carInfo: {
    brand: string;
    model: string;
    plate?: string;
  };
}

export const ExtendRentalModal: React.FC<ExtendRentalModalProps> = ({
  isOpen,
  onClose,
  onExtend,
  currentEndDate,
  carInfo
}) => {
  const [newEndDate, setNewEndDate] = useState(() => {
    const safeCurrentDate = parseDate(currentEndDate);
    return new Date(safeCurrentDate.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  });
  const [newEndTime, setNewEndTime] = useState('18:00');

  // Quick extension options
  const quickExtendOptions = [
    { label: '1 Hour', hours: 1 },
    { label: '4 Hours', hours: 4 },
    { label: '1 Day', hours: 24 },
    { label: '3 Days', hours: 72 },
    { label: '1 Week', hours: 168 }
  ];

  const handleQuickExtend = (hours: number) => {
    const safeCurrentDate = parseDate(currentEndDate);
    const extendedDate = new Date(safeCurrentDate.getTime() + (hours * 60 * 60 * 1000));
    setNewEndDate(extendedDate.toISOString().split('T')[0]);
    setNewEndTime(extendedDate.toTimeString().split(':').slice(0, 2).join(':'));
  };

  const handleExtend = () => {
    const selectedDate = new Date(newEndDate);
    onExtend(selectedDate, newEndTime);
    onClose();
  };

  const handleCancel = () => {
    // Reset to default values
    const safeCurrentDate = parseDate(currentEndDate);
    setNewEndDate(new Date(safeCurrentDate.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    setNewEndTime('18:00');
    onClose();
  };

  if (!isOpen) return null;

  const today = new Date().toISOString().split('T')[0];
  const minDate = newEndDate < today ? today : newEndDate;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 text-white rounded-t-2xl">
          <h2 className="text-xl font-semibold">Extend Rental Period</h2>
          <p className="text-orange-100 text-sm mt-1">
            {carInfo.brand} {carInfo.model} {carInfo.plate && `(${carInfo.plate})`}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Rental Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Current Rental</h3>
            <p className="text-sm text-gray-600">
              Ends: {formatDate(currentEndDate)} at {parseDate(currentEndDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>

          {/* Quick Extension Options */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">🚀 Quick Extension</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {quickExtendOptions.map((option) => (
                <button
                  key={option.label}
                  onClick={() => handleQuickExtend(option.hours)}
                  className="px-3 py-2 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 hover:border-orange-300 transition-all duration-200 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                >
                  +{option.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">💡 Click for instant extension, or set custom date/time below</p>
          </div>

          {/* Custom Date & Time */}
          <div className="border-t pt-4">
            <h3 className="font-medium text-gray-900 mb-3">🗓️ Custom Extension</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* New End Date */}
              <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New End Date
            </label>
            <input
              type="date"
              value={newEndDate}
              onChange={(e) => setNewEndDate(e.target.value)}
              min={minDate}
              className="w-full p-3 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
            />
              </div>

              {/* New End Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New End Time
                </label>
                <input
                  type="time"
                  value={newEndTime}
                  onChange={(e) => setNewEndTime(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                />
              </div>
            </div>
          </div>

          {/* Extension Summary */}
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <h4 className="font-medium text-orange-900 mb-2">Extension Summary</h4>
            <div className="text-sm text-orange-700 space-y-1">
              <p>Extended until: {formatDate(newEndDate)} at {newEndTime}</p>
              <p className="font-medium">
                Additional days: {calculateDays(currentEndDate, newEndDate)}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t rounded-b-2xl">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          
          <button
            onClick={handleExtend}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Extend Rental
          </button>
        </div>
      </div>
    </div>
  );
};
